import React, { Component, useEffect } from "react";
import axios from "axios";

const API = '/api';

/**
 * Report error to backend silently.
 * Never throws — fire and forget.
 */
function reportError(errorMessage, errorStack, errorType, url) {
  try {
    const payload = {
      error_message: String(errorMessage || "Unknown error").slice(0, 1000),
      error_stack: String(errorStack || "").slice(0, 3000),
      error_type: String(errorType || "unknown").slice(0, 100),
      url: String(url || window.location.pathname).slice(0, 500),
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    // Use navigator.sendBeacon for reliability (works even during page unload)
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/log/client-error`, blob);
    } else {
      // Fallback to fetch (fire-and-forget)
      fetch(`${API}/log/client-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never let error reporting itself throw
  }
}

/**
 * Hook to install global error handlers (window.onerror, unhandledrejection).
 * Call this once at the top of your app.
 */
export function useGlobalErrorHandler() {
  useEffect(() => {
    // Catch synchronous JS errors
    const handleError = (event) => {
      reportError(
        event.message || event.error?.message || "Unhandled error",
        event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
        "window.onerror",
        event.filename || window.location.pathname
      );
    };

    // Catch unhandled promise rejections
    const handleRejection = (event) => {
      const reason = event.reason;
      reportError(
        reason?.message || String(reason || "Unhandled rejection"),
        reason?.stack || "",
        "unhandledrejection",
        window.location.pathname
      );
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);
}

/**
 * React Error Boundary that catches render errors and reports them.
 * Auto-reloads once for chunk loading failures (stale deploy).
 */
export class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[RVINCENTE] GlobalErrorBoundary caught:', error?.message, error?.stack?.slice(0, 200));
    
    reportError(
      error?.message || "React render error",
      `${error?.stack || ""}\n\nComponent Stack: ${errorInfo?.componentStack || ""}`,
      "ReactErrorBoundary",
      window.location.pathname
    );

    // Auto-reload once for chunk loading failures (including HTML-instead-of-JS errors)
    const msg = error?.message || '';
    const isChunkError = 
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes('ChunkLoadError') ||
      msg.includes("Unexpected token '<'") ||
      msg.includes("Unexpected token 'export'") ||
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('is not a function') ||
      msg.includes('is not defined') ||
      (error?.name === 'ChunkLoadError');
    
    if (isChunkError) {
      console.warn('[RVINCENTE] Detected chunk/module error, attempting recovery...');
      const lastReload = sessionStorage.getItem('chunk_reload');
      const now = Date.now();
      if (!lastReload || (now - parseInt(lastReload, 10)) > 30000) {
        sessionStorage.setItem('chunk_reload', String(now));
        // Clear caches and unregister SW THEN reload
        Promise.all([
          ('caches' in window) ? caches.keys().then(names => Promise.all(names.map(n => caches.delete(n)))) : Promise.resolve(),
          ('serviceWorker' in navigator) ? navigator.serviceWorker.getRegistrations().then(regs => Promise.all(regs.map(r => r.unregister()))) : Promise.resolve()
        ]).finally(() => {
          window.location.reload();
        });
        return;
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', background: '#1A1A1A', color: 'white',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '20px', textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '20px', marginBottom: '10px' }}>Si è verificato un errore</h2>
          <p style={{ color: '#999', fontSize: '14px', marginBottom: '20px' }}>
            {this.state.error?.message}
          </p>
          <button
            onClick={() => {
              if ('caches' in window) {
                caches.keys().then(names => names.forEach(n => caches.delete(n)));
              }
              window.location.reload();
            }}
            style={{
              padding: '12px 24px', background: '#F5A623', color: '#1A1A1A',
              border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer'
            }}
          >
            Ricarica App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
